import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { PHYSIO_STATUS, fmtDateEs } from "@/lib/physio";

const StatusBadge = ({ status }) => {
  const st = PHYSIO_STATUS[status] || PHYSIO_STATUS.booked;
  const Icon = st.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${st.badge}`}>
      <Icon className="h-3 w-3" /> {st.label}
    </Badge>
  );
};

export default function PhysioHistoryDialog({ open, onOpenChange, playerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !playerId) return;
    setLoading(true);
    api.get(`/physio/players/${playerId}/history`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, playerId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="physio-history-dialog">
        <DialogHeader>
          <DialogTitle>Historial de fisioterapia{data?.player?.name ? ` · ${data.player.name}` : ""}</DialogTitle>
          <DialogDescription>Todas las citas del jugador en orden cronológico.</DialogDescription>
        </DialogHeader>

        {loading && <div className="text-sm text-slate-500">Cargando…</div>}
        {data && (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
              {["booked", "confirmed", "completed", "cancelled", "no_show"].map((k) => (
                <div key={k} className="rounded-lg border border-slate-200 p-2 text-center">
                  <div className="text-lg font-black" style={{ fontFamily: "Nunito" }}>{data.counts?.[k] ?? 0}</div>
                  <div className="text-[10px] uppercase text-slate-500">{PHYSIO_STATUS[k].label}</div>
                </div>
              ))}
            </div>
            {data.next && (
              <div className="rounded-lg bg-purple-50 border border-purple-200 p-2 mb-3 text-sm">
                <span className="font-bold text-purple-800">Próxima cita:</span> {fmtDateEs(data.next.date)} · {data.next.start_time}–{data.next.end_time}
              </div>
            )}
            <div className="space-y-2">
              {(data.items || []).length === 0 && <div className="text-sm text-slate-500">Sin citas registradas.</div>}
              {(data.items || []).map((a) => (
                <div key={a.id} className="rounded-lg border border-slate-200 p-3" data-testid={`physio-history-item-${a.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold">{fmtDateEs(a.date)} · {a.start_time}–{a.end_time}</div>
                    <StatusBadge status={a.status} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Equipo: {a.team_name || "—"}{a.category ? ` · ${a.category}` : ""}
                    {a.reason ? ` · Motivo: ${a.reason}` : ""}{a.body_area ? ` · Zona: ${a.body_area}` : ""}
                  </div>
                  {a.internal_notes && <div className="text-xs text-slate-600 mt-1 italic">“{a.internal_notes}”</div>}
                  <div className="text-[10px] text-slate-400 mt-1">
                    Creada por {a.created_by_name || "—"}{a.updated_by_name ? ` · Últ. cambio: ${a.updated_by_name}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
